import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BudgetEntry {
  id: string;
  account_code: string | null;
  country: string | null;
  year: number;
  month: number;
  planned_amount: number;
  currency: string;
  notes: string | null;
  assumptions: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BudgetEntryWithAccount extends BudgetEntry {
  account?: {
    code: string;
    name: string;
    type: string;
    category: string | null;
  };
}

export function useBudgetEntries(year: number, country?: string) {
  return useQuery({
    queryKey: ['budget', year, country],
    queryFn: async () => {
      let query = supabase
        .from('budget_entries')
        .select(`
          *,
          account:chart_of_accounts(code, name, type, category)
        `)
        .eq('year', year)
        .order('account_code');

      if (country && country !== 'all') {
        query = query.eq('country', country);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BudgetEntryWithAccount[];
    },
  });
}

export function useBudgetByMonth(year: number, month: number, country?: string) {
  return useQuery({
    queryKey: ['budget-month', year, month, country],
    queryFn: async () => {
      let query = supabase
        .from('budget_entries')
        .select(`
          *,
          account:chart_of_accounts(code, name, type, category)
        `)
        .eq('year', year)
        .eq('month', month);

      if (country && country !== 'all') {
        query = query.eq('country', country);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BudgetEntryWithAccount[];
    },
  });
}

interface CreateBudgetData {
  account_code: string;
  country: string;
  year: number;
  month: number;
  planned_amount: number;
  currency?: string;
  notes?: string;
  assumptions?: string;
}

export function useCreateBudgetEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBudgetData) => {
      const { data: result, error } = await supabase
        .from('budget_entries')
        .insert({
          account_code: data.account_code,
          country: data.country,
          year: data.year,
          month: data.month,
          planned_amount: data.planned_amount,
          currency: data.currency || 'USD',
          notes: data.notes,
          assumptions: data.assumptions,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget'] });
      queryClient.invalidateQueries({ queryKey: ['budget-month'] });
    },
  });
}

export function useUpdateBudgetEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<BudgetEntry> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('budget_entries')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget'] });
      queryClient.invalidateQueries({ queryKey: ['budget-month'] });
    },
  });
}

export function useUpsertBudgetEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBudgetData) => {
      const { data: result, error } = await supabase
        .from('budget_entries')
        .upsert(
          {
            account_code: data.account_code,
            country: data.country,
            year: data.year,
            month: data.month,
            planned_amount: data.planned_amount,
            currency: data.currency || 'USD',
            notes: data.notes,
            assumptions: data.assumptions,
          },
          { onConflict: 'account_code,country,year,month' }
        )
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget'] });
      queryClient.invalidateQueries({ queryKey: ['budget-month'] });
    },
  });
}

export function useDeleteBudgetEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('budget_entries')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget'] });
      queryClient.invalidateQueries({ queryKey: ['budget-month'] });
    },
  });
}

// Copy budget from previous year
export function useCopyBudgetFromYear() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ sourceYear, targetYear, country }: { 
      sourceYear: number; 
      targetYear: number; 
      country?: string;
    }) => {
      let query = supabase
        .from('budget_entries')
        .select('*')
        .eq('year', sourceYear);

      if (country && country !== 'all') {
        query = query.eq('country', country);
      }

      const { data: sourceEntries, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      if (!sourceEntries || sourceEntries.length === 0) {
        throw new Error('Nenhuma entrada de orçamento encontrada no ano de origem');
      }

      const newEntries = sourceEntries.map(entry => ({
        account_code: entry.account_code,
        country: entry.country,
        year: targetYear,
        month: entry.month,
        planned_amount: entry.planned_amount,
        currency: entry.currency,
        notes: entry.notes,
        assumptions: entry.assumptions,
      }));

      const { error: insertError } = await supabase
        .from('budget_entries')
        .upsert(newEntries, { onConflict: 'account_code,country,year,month' });

      if (insertError) throw insertError;

      return { copied: newEntries.length };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget'] });
    },
  });
}
